import type { ReactNode } from 'react';
import { Badge, Button, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import TextContentSep from '../common/TextContentSep';

type Props = {
  iconClassName: string;
  title: string;
  description: ReactNode;
  copied: boolean;
  onCopy: () => void;
  heroClassName?: string;
  copyButtonClassName?: string;
  guestBadgeClassName?: string;
  descriptionClassName?: string;
};

/**
 * 公開共有ページ共通ヒーロー。
 * Badge は h1 の外（読み上げでタイトルと「ログイン不要」が連結しない）。
 */
export default function PublicShareHero({
  iconClassName,
  title,
  description,
  copied,
  onCopy,
  heroClassName = 'public-sample-hero',
  copyButtonClassName = 'public-sample-copy-btn',
  guestBadgeClassName,
  descriptionClassName = 'mb-0 opacity-75 small d-none d-md-block',
}: Props) {
  const { t } = useTranslation();
  const guestBadge = t('sample.location_share_guest_badge', { defaultValue: 'ログイン不要' });
  const copyShort = copied
    ? t('sample.url_copied_short', { defaultValue: 'コピー済み' })
    : t('sample.copy_url', { defaultValue: 'URLをコピー' });
  const copyTitle = copied
    ? t('sample.url_copied', { defaultValue: 'URLをクリップボードにコピーしました' })
    : t('sample.copy_url', { defaultValue: 'URLをコピー' });

  return (
    <div className={`bg-primary text-white ${heroClassName} mb-3 mb-md-4`}>
      <Container>
        <Row className="align-items-center g-2">
          <Col xs={12} md={8} className="min-w-0">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-0 mb-md-1">
              <h1 className="h5 mb-0 text-truncate">
                <i className={`fas ${iconClassName} me-2`} aria-hidden="true" />
                {title}
              </h1>
              <TextContentSep />
              <Badge
                bg="light"
                text="dark"
                className={['fw-normal', guestBadgeClassName].filter(Boolean).join(' ')}
              >
                {guestBadge}
              </Badge>
            </div>
            <div className={descriptionClassName}>{description}</div>
          </Col>
          <Col xs={12} md={4} className="text-md-end">
            <Button
              variant="light"
              size="sm"
              className={copyButtonClassName}
              onClick={onCopy}
              aria-label={copyShort}
              title={copyTitle}
            >
              <i className={`fas ${copied ? 'fa-check' : 'fa-copy'} me-1`} aria-hidden="true" />
              {copyShort}
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
